import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { games } from '@/mocks/games';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';
  const [animateKey, setAnimateKey] = useState(0);

  const currentCategory = categories.find((c) => c.slug === activeTab) || categories[0];

  const filteredGames = activeTab === 'all'
    ? games
    : games.filter((g) => g.category === activeTab);

  const handleTabChange = (slug: string) => {
    setSearchParams({ tab: slug });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setAnimateKey((prev) => prev + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <PageSEO
        title={`${currentCategory.name} - galagame 游戏分类 | 发现你的专属游戏乐趣`}
        description={`galagame ${currentCategory.name}专区，${currentCategory.description}。浏览 galagame 平台精选游戏，找到最适合你的游戏体验。`}
        keywords={`galagame,galagame游戏,galagame${currentCategory.name},galagame游戏分类,galagame下载`}
        canonicalUrl={`https://mkgcgcb.cn/category?tab=${activeTab}`}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '游戏分类' },
          ]}
        />

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100">
                游戏分类
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex items-center justify-center gap-1.5 mb-10 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-100 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop key={animateKey}>
              {filteredGames.map((game) => (
                <div
                  key={game.id}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/50 hover:border-primary-500/30 transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={game.image}
                      alt={game.name}
                      title={`${game.name} - galagame ${currentCategory.name}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent"></div>
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
                      {game.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-primary-500/80 text-background-50"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-100">
                      {game.name}
                    </h3>
                    <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {game.description}
                    </p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/30">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-xs text-primary-500"></i>
                        <span className="text-xs text-foreground-300 font-medium">{game.rating}</span>
                      </div>
                      <span className="text-xs text-foreground-500">
                        <i className="ri-user-line mr-1"></i>
                        {game.players}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredGames.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-gamepad-line text-2xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-500">该分类暂未上架游戏，敬请期待</p>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 mt-4 px-5 py-2 text-sm font-medium text-primary-500 border border-primary-500/30 hover:bg-primary-500/10 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  查看全部游戏
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}